const createTrace = (start, end, result, kind) => ({ start, end, result, kind });
/// Basic nodes ///
/**
 * We use this node as sentry in case the parsed expression had an error
 */
export class TrueNode {
    constructor(start = 0, end = 0) {
        this.start = start;
        this.end = end;
    }
    evaluate(_context) { return true; }
    evaluateWithTrace(_context) {
        return {
            result: true,
            traces: [createTrace(this.start, this.end, true, "literal")]
        };
    }
}
/**
 * We use this node as sentry in case the parsed expression had an error
 */
export class FalseNode {
    constructor(start = 0, end = 0) {
        this.start = start;
        this.end = end;
    }
    evaluate(_context) { return false; }
    evaluateWithTrace(_context) {
        return {
            result: false,
            traces: [createTrace(this.start, this.end, false, "literal")]
        };
    }
}
/// Statement operators ///
/**
 * The ObjectStateOperator evaluates the statement `objectId: objectState`. It returns  true if the object has the
 * respective state or false otherwise. It also returns false if the objectId is not present in the provided context
 */
export class ObjectStateOperator {
    constructor(objectId, objectState, start, end) {
        this.objectId = objectId;
        this.objectState = objectState;
        this.start = start;
        this.end = end;
    }
    evaluate(context) {
        const scenarioObject = context.trainingObjectMap.get(this.objectId);
        if (scenarioObject === undefined)
            return false;
        const currentState = scenarioObject.getCurrentState();
        if (currentState === undefined)
            return false;
        return currentState.name === this.objectState;
    }
    evaluateWithTrace(context) {
        const result = this.evaluate(context);
        return {
            result,
            traces: [createTrace(this.start, this.end, result, "leaf")]
        };
    }
}
/**
 * The ObjectAtPositionOperator evaluates if a target object is at the position of a reference object. It returns false
 * if any of the two objects are not in the context
 */
export class ObjectAtPositionOperator {
    constructor(objectId, referenceObjectId, errorMargin = 1e-3, start = 0, end = 0) {
        this.objectId = objectId;
        this.referenceObjectId = referenceObjectId;
        this.errorMargin = errorMargin;
        this.start = start;
        this.end = end;
    }
    evaluate(context) {
        const scenarioObject = context.trainingObjectMap.get(this.objectId);
        const referenceObject = context.trainingObjectMap.get(this.referenceObjectId);
        if (scenarioObject === undefined || referenceObject === undefined)
            return false;
        // Position should be correct up to a specified error margin
        return scenarioObject.position.distanceTo(referenceObject.position) < this.errorMargin;
    }
    evaluateWithTrace(context) {
        const result = this.evaluate(context);
        return {
            result,
            traces: [createTrace(this.start, this.end, result, "leaf")]
        };
    }
}
export class ObjectCollisionOperator {
    constructor(objectId, referenceObjectId, start, end) {
        this.objectId = objectId;
        this.referenceObjectId = referenceObjectId;
        this.start = start;
        this.end = end;
    }
    evaluate(context) {
        const objectA = context.trainingObjectMap.get(this.objectId);
        const objectB = context.trainingObjectMap.get(this.referenceObjectId);
        if (objectA === undefined || objectB === undefined)
            return false;
        // Position should be correct up to a specified error margin
        const objectCollisions = context.trainingEventMetaInformation.get("OBJECT_COLLISION_LIST");
        if (objectCollisions === undefined)
            return false;
        const key = objectA.id + " | " + objectB.id;
        const reverseKey = objectB.id + " | " + objectA.id;
        const conditionA = objectCollisions.has(key);
        const conditionB = objectCollisions.has(reverseKey);
        return conditionA || conditionB;
    }
    evaluateWithTrace(context) {
        const result = this.evaluate(context);
        return {
            result,
            traces: [createTrace(this.start, this.end, result, "leaf")]
        };
    }
}
/**
 * The ObjectIsAncestorOperator evaluates if a first object is an ancestor of the second object. It returns false
 * if any of the two objects are not in the context
 */
export class ObjectIsAncestorOperator {
    constructor(ancestorObjectId, descendantObjectId, start, end) {
        this.ancestorObjectId = ancestorObjectId;
        this.descendantObjectId = descendantObjectId;
        this.start = start;
        this.end = end;
    }
    evaluate(context) {
        const ancestorObject = context.trainingObjectMap.get(this.ancestorObjectId);
        const descendantObject = context.trainingObjectMap.get(this.descendantObjectId);
        if (ancestorObject === undefined || descendantObject === undefined)
            return false;
        let attachmentsMap = context.trainingEventMetaInformation.get("ATTACHMENTS_MAP");
        if (attachmentsMap === undefined) {
            attachmentsMap = new Map();
            context.trainingEventMetaInformation.set("ATTACHMENTS_MAP", attachmentsMap);
        }
        let currentObjectId = descendantObject.id;
        while (currentObjectId !== undefined) {
            const attachment = attachmentsMap.get(currentObjectId);
            if (attachment === undefined)
                return false;
            if (attachment.parent === ancestorObject.id)
                return true;
            currentObjectId = attachment.parent;
        }
        return false;
    }
    evaluateWithTrace(context) {
        const result = this.evaluate(context);
        return {
            result,
            traces: [createTrace(this.start, this.end, result, "leaf")]
        };
    }
}
/// Unary expression nodes ///
export class NotExpression {
    constructor(expression, start, end) {
        this.expression = expression;
        this.start = start !== null && start !== void 0 ? start : expression.start;
        this.end = end !== null && end !== void 0 ? end : expression.end;
    }
    evaluate(context) {
        return !this.expression.evaluate(context);
    }
    evaluateWithTrace(context) {
        const childResult = this.expression.evaluateWithTrace(context);
        const result = !childResult.result;
        return {
            result,
            traces: [
                ...childResult.traces,
                createTrace(this.start, this.end, result, "not")
            ]
        };
    }
}
/// Binary expression nodes ///
export class AndExpression {
    constructor(left, right) {
        this.left = left;
        this.right = right;
        this.start = left.start;
        this.end = right.end;
    }
    evaluate(context) {
        const leftResult = this.left.evaluate(context);
        const rightResult = this.right.evaluate(context);
        return leftResult && rightResult;
    }
    evaluateWithTrace(context) {
        const leftResult = this.left.evaluateWithTrace(context);
        const rightResult = this.right.evaluateWithTrace(context);
        const result = leftResult.result && rightResult.result;
        return {
            result,
            traces: [
                ...leftResult.traces,
                ...rightResult.traces,
                createTrace(this.start, this.end, result, "and")
            ]
        };
    }
}
export class OrExpression {
    constructor(left, right) {
        this.left = left;
        this.right = right;
        this.start = left.start;
        this.end = right.end;
    }
    evaluate(context) {
        const leftResult = this.left.evaluate(context);
        const rightResult = this.right.evaluate(context);
        return leftResult || rightResult;
    }
    evaluateWithTrace(context) {
        const leftResult = this.left.evaluateWithTrace(context);
        const rightResult = this.right.evaluateWithTrace(context);
        const result = leftResult.result || rightResult.result;
        return {
            result,
            traces: [
                ...leftResult.traces,
                ...rightResult.traces,
                createTrace(this.start, this.end, result, "or")
            ]
        };
    }
}
